import { Nullable, Timestamp } from '@/types';
export type DiscussionPoint = {
  name: string | null;
};

export type AgendaItem = {
  itemNo: string;
  topic: string;
  discussion_points: DiscussionPoint[] | null | undefined;
};

export type Meeting = {
  id: string;
  meeting_name: string;
  status: string;
  category: string;
  agenda: AgendaItem[] | undefined | null;
  created_at: Nullable<Timestamp>;
  updated_at: Nullable<Timestamp>;
  deleted_at: Nullable<Timestamp>;
};

export type GroupedMeeting = {
  category: string;
  list: {
    id: string;
    category: string;
    meeting_name: string;
    status: string;
    agenda: AgendaItem[] | null; // Ensure compatibility with Meeting type
  }[];
};

export type Category = {
  type: 'category';
  name: string;
  id: string;
};

export type Option = {
  type: 'options';
  name: string;
  id: string;
};

export const groupMeetingsByCategory = (
  meetings: Meeting[],
): GroupedMeeting[] => {
  const grouped = meetings.reduce(
    (acc: Record<string, GroupedMeeting['list']>, meeting) => {
      const { category, meeting_name, agenda, id, status } = meeting;

      if (!acc[category]) {
        acc[category] = [];
      }

      acc[category].push({
        id,
        category,
        meeting_name,
        status,
        agenda: agenda || null, // Assign null if agenda is undefined
      });

      return acc;
    },
    {},
  );

  return Object.keys(grouped).map((category) => ({
    category,
    list: grouped[category],
  }));
};

export const generateOptions = (
  groupedData: GroupedMeeting[],
): Array<Category | Option> => {
  return groupedData.reduce<Array<Category | Option>>((acc, curr) => {
    // Create a category object
    const category: Category = {
      type: 'category',
      name: curr.category,
      id: curr.category,
    };

    // Map options for each meeting in the current category
    const options: Option[] = curr.list.map((item) => ({
      type: 'options',
      name: item.meeting_name,
      status: item.status,
      id: item.id,
    }));

    // Combine the category and its options
    return [...acc, category, ...options];
  }, []);
};
